import { useState } from 'react';
import type { User } from 'firebase/auth';
import { api } from '@/lib/api';
import { useAuth } from './auth';
import type { AuthContext } from '@/context';
import { apiInvoke } from '@/store/helpers/load';
import { useConfig, type ApiConfig } from './config';

export function useApi(): API {
  const { api: config } = useConfig();
  const [isLoading, setIsLoading] = useState(false);

  async function callApi(url: string, user: User | null, init: RequestInit) {
    setIsLoading(true);
    try {
      return await api(url, user, init, {
        baseUrl: config.baseUrl,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return {
    tenant: config.tenant,
    baseUrl: config.baseUrl,
    location: config.location,
    api: callApi,
    isLoading,
  };
}

export type APIDispatch = (
  url: string,
  user: User | null,
  init: RequestInit,
) => Promise<Response>;

export type API = {
  api: APIDispatch;
  isLoading: boolean;
} & ApiConfig;

export type APIWithAuth = API & {
  auth: AuthContext;
};

export function useApiWithAuth() {
  const api = useApi();
  const auth = useAuth();

  return {
    ...api,
    auth,
    invoke: (url: string, init?: RequestInit) =>
      apiInvoke(api.api, url, auth, init),
  };
}
